import { useDeviceView } from "../lib/DeviceViewContext";
import { FaceIdCard } from "../components/account/FaceId";
import { QrDeviceCard } from "../components/account/QrLogin";
import { SignedInDevicesCard } from "../components/account/SignedInDevices";
import { setTimePref, hydrateTimePrefs } from "../lib/timePrefs";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { User, Bell, Shield, Check, LogOut, CreditCard, Ruler, SlidersHorizontal, PoundSterling, BarChart3, Target, Settings, Briefcase, Trophy, HardDrive, Mail, Users, Monitor, Smartphone, ClipboardCheck } from "lucide-react";
import { useHeightUnit } from "../lib/units";
import { getAccount, updateAccount, api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { TeamBadge } from "../components/TeamBadge";
import { TEAM_INFO } from "../lib/teamMap";
import { Loader } from "../components/Loader";
import AccountsAdmin from "../components/admin/AccountsAdmin";
import SettingsAdmin from "../components/admin/SettingsAdmin";
import { ChevronLeft, HelpCircle, Disc3, Wrench, UserCircle } from "lucide-react";
import { useAdminMode } from "../lib/AdminModeContext";
import { useTour } from "../lib/TourContext";


import MyFinances from "../components/account/MyFinances";
import Reports from "../components/account/Reports";
import { usePagePrefs } from "../lib/PagePrefs";
import FavouritesSection from "../components/account/FavouritesSection";
import { FollowedLeagues } from "../components/account/FollowedLeagues";
import OwnerPushSection from "../components/account/OwnerPushSection";
import StorageCentre from "./StorageCentre";
import Leagues from "./Leagues";
import { TeamGrid, GroupPills, LeagueSwitch, AllowNotifications, useTeamGroups } from "../components/account/UserSetup";
import { useNotifPick } from "../lib/useNotifPick";
import { useFavourite } from "../lib/useFavourite";


const Glass = ({ className = "", children, ...rest }) => (
    <div {...rest} className={`rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.35)] ${className}`}>{children}</div>
);
const Card = ({ icon: Icon, title, right = null, children, testid, className = "" }) => (
    <div className={`rounded-2xl p-6 sm:p-8 space-y-4 shadow-2xl border border-white/10 ${className}`} style={{ background: "#0B0D14" }} data-testid={testid}>
        <div className="text-center">
            {Icon && <Icon className="w-5 h-5 text-[#9D4CDD] mx-auto mb-1.5" />}
            <h2 className="font-heading text-xl font-black uppercase tracking-tight text-white">{title}</h2>
            {right && <div className="mt-2 flex justify-center">{right}</div>}
        </div>
        {children}
    </div>
);

// Game alerts: same team grid as Setup — tap the teams you want goal & game alerts for.
function AlertTeams() {
    const notif = useNotifPick();
    const groups = useTeamGroups();
    const [lg, setLg] = useState("pwhl");
    return (
        <div className="mx-auto max-w-md space-y-3" data-testid="account-alert-teams">
            <AllowNotifications />
            <p className="text-xs text-white/50 text-center">Tap any teams you want goal & game alerts for.</p>
            <LeagueSwitch league={lg} setLeague={setLg} testid="account-alert-league" />
            <GroupPills groups={groups[lg]} isOn={(id) => notif.isOn(lg, id)} onSet={(ids, on) => notif.setMany(lg, ids, on)} testid="account-alert-group" />
            <TeamGrid league={lg} isOn={(id) => notif.isOn(lg, id)} onPick={(id) => notif.toggle(lg, id)} testPrefix="account-alert" />
        </div>
    );
}

const SmallPill = ({ on, onClick, children, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid}
        className={`px-2.5 py-1.5 rounded-lg border text-[10px] font-black uppercase tracking-wider transition-colors ${on ? "bg-[#9D4CDD] border-[#9D4CDD] text-white" : "bg-black/30 border-white/10 text-white/65 hover:text-white hover:border-white/25"}`}>{children}</button>
);
// Each preference is its own small square: title on top, options underneath.
const PrefRow = ({ label, hint, children, testid }) => (
    <div className="rounded-xl border border-white/10 p-3 flex flex-col gap-2" style={{ background: "#0B0D14" }} data-testid={testid}>
        <div className="min-w-0"><div className="text-xs font-bold text-white">{label}</div>{hint && <div className="text-[10px] text-white/40 truncate">{hint}</div>}</div>
        <div className="flex flex-wrap gap-1">{children}</div>
    </div>
);
const fmtSecs = (x) => { x = Math.round(x || 0); if (x < 60) return `${x}s`; const m = Math.floor(x / 60); return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`; };
// Account header: who you are + your account history (was the switchboard overlay).
function AccountProfile({ account, user }) {
    const { data: pageStats = [] } = useQuery({ queryKey: ["my-page-stats"], queryFn: () => api.get("/account/page-stats").then((r) => r.data), enabled: !!user });
    const p = account?.profile || {};
    const team = p.main_team_id ? TEAM_INFO[p.main_team_id] : null;
    const c = team?.color || "#60A5FA";
    const d = (iso) => { const x = iso ? new Date(iso) : null; return x && !Number.isNaN(x.getTime()) ? x.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—"; };
    const info = [["Member Since", d(p.created_at)], ["Last Sign In", d(p.last_login)], ["Favourite Team", team?.abbr || "—"], ["Pages Viewed", pageStats.length], ["Time Tracked", fmtSecs(pageStats.reduce((a, x) => a + (x.seconds || 0), 0))]];
    return (
        <div className="relative rounded-2xl border shadow-2xl p-4 overflow-hidden" style={{ background: "#0B0D14", borderColor: `${c}55` }} data-testid="account-profile">
            <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(90% 120% at 0% 0%, ${c}30 0%, transparent 60%)` }} />
            <div className="relative flex items-center gap-3">
                <span className="h-11 w-11 shrink-0 rounded-full bg-black border border-white/15 flex items-center justify-center overflow-hidden">
                    {p.main_team_id ? <span style={{ transform: "scale(0.75)" }}><TeamBadge teamId={p.main_team_id} size="lg" asLink={false} /></span> : <UserCircle className="h-6 w-6 text-white/40" />}
                </span>
                <div className="min-w-0">
                    <div className="text-base font-extrabold text-white truncate" data-testid="account-profile-name">{p.name || (user?.email || "").split("@")[0]}{p.role && p.role !== "user" && <span className="text-white/45 font-bold text-sm"> · {p.role[0].toUpperCase() + p.role.slice(1)}</span>}</div>
                    <div className="text-xs text-white/55 truncate">{p.email || user?.email}</div>
                </div>
            </div>
            <div className="relative grid grid-cols-5 gap-1.5 mt-3" data-testid="account-profile-info">
                {info.map(([l, v]) => (
                    <div key={l} className="min-w-0 rounded-lg border border-white/10 bg-black/35 px-2 py-1.5">
                        <div className="text-[12px] font-black text-white leading-tight truncate">{v}</div>
                        <div className="text-[8px] uppercase tracking-wider text-white/50 font-bold truncate">{l}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Avatar is ALWAYS the favourite team crest, or a plain black disc when none.
function Avatar({ teamId, size = 96 }) {
    if (teamId) return (
        <div className="rounded-full bg-[#0A0B10] border border-white/10 flex items-center justify-center overflow-hidden ring-2 ring-white/20 shrink-0" style={{ width: size, height: size }} data-testid="account-avatar">
            <div style={{ transform: `scale(${size / 64})` }}><TeamBadge teamId={teamId} size="xl" asLink={false} /></div>
        </div>
    );
    return <div className="rounded-full bg-black border border-white/10 ring-2 ring-white/20 shrink-0" style={{ width: size, height: size }} data-testid="account-avatar" />;
}

export default function MyAccount({ embedded = false, onClose, tab: embTab, onTab, switcher = null } = {}) {
    const [heightUnit, setHeightUnit] = useHeightUnit();
    const { user, loading, logout, isOwner, hasAdminAccess, canBusiness, canCreate } = useAuth();
    const tour = useTour();
    const { allButtons, toggleAllButtons } = useAdminMode();
    const { getPref, setPref } = usePagePrefs();
    const { setPcRes } = useDeviceView();
    const extOn = getPref("page_toggle", "nhl_sched_side_lineups") === "on";
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [params, setParams] = useSearchParams();
    const rawTab = (onTab ? embTab : params.get("tab")) || "account";
    // Owner-only + secret (URL) views.
    const OWNER_VIEWS = new Set(["finances", "subscription", "reports", "settings", "storage", "leagues"]);
    const SECRET_VIEWS = new Set(["owner", "accounts"]);
    let view = rawTab;
    if (OWNER_VIEWS.has(view) && !isOwner) view = "home";
    if (view === "owner" && !isOwner) view = "home";
    if (view === "accounts" && !(isOwner || hasAdminAccess)) view = "home";
    if (!["account", "home", "notifications", "objectives", "preferences", ...OWNER_VIEWS, ...SECRET_VIEWS].includes(view)) view = "account";
    const setView = (k) => (onTab ? onTab(k) : setParams((p) => { p.set("tab", k); return p; }, { replace: true }));
    const toggleView = (k) => setView(view === k ? "home" : k);
    const doSignOut = () => { if (window.confirm("Are you sure you want to sign out?")) { logout(); navigate("/"); } };

    const { data: account, isLoading } = useQuery({ queryKey: ["account"], queryFn: getAccount, enabled: !!user });
    const { data: nhlTeams = [] } = useQuery({ queryKey: ["nhl-teams"], queryFn: () => api.get("/nhl/teams").then((r) => r.data), enabled: !!user && isOwner, staleTime: 60 * 60 * 1000 });

    const [form, setForm] = useState(null);
    useEffect(() => {
        if (account) {
            setForm({ main_team_id: account.profile.main_team_id || "", main_nhl_team_id: account.profile.main_nhl_team_id || "", fav_league: account.profile.fav_league || "", notif: account.notif || {}, push_prefs: account.profile.push_prefs || {}, time_format: account.profile.time_format || "12h", clock_mode: account.profile.clock_mode || "elapsed", tz_label: account.profile.tz_label || "off", objectives: account.profile.objectives || {} });
            hydrateTimePrefs(account.profile);
        }
    }, [account]);

    const save = useMutation({ mutationFn: (body) => updateAccount(body), onSuccess: () => { qc.invalidateQueries({ queryKey: ["account"] }); toast.success("Saved"); }, onError: () => toast.error("Could not save") });
    const { setFavourite } = useFavourite();
    const pickTeam = (tid) => { setForm((s) => ({ ...s, main_team_id: tid })); setFavourite("pwhl", tid || null); save.mutate({ main_team_id: tid, hero_style: "original" }); };
    const pickTimeFormat = (v) => { setForm((s) => ({ ...s, time_format: v })); setTimePref("time_format", v); save.mutate({ time_format: v }); };
    const pickTzLabel = (v) => { setForm((s) => ({ ...s, tz_label: v })); setTimePref("tz_label", v); save.mutate({ tz_label: v }); };
    const pickClockMode = (v) => { setForm((s) => ({ ...s, clock_mode: v })); setTimePref("clock_mode", v); save.mutate({ clock_mode: v }); };

    if (loading) return <Loader />;
    if (!user) return (
        <div className="max-w-xl mx-auto px-4 py-24 text-center">
            <User className="w-12 h-12 mx-auto text-[#9D4CDD] mb-4" />
            <h1 className="text-4xl font-extrabold text-white mb-2">My Account</h1>
            <p className="text-white/60 mb-6">Sign in to manage your favourite team and alerts.</p>
            <Link to="/login" className="inline-flex px-5 py-2.5 rounded-md bg-[#9D4CDD] hover:bg-[#B366F0] text-white font-bold uppercase tracking-wider text-sm" data-testid="account-signin-cta">Sign in</Link>
        </div>
    );
    if (isLoading || !form) return <Loader />;

    const tzLabel = (() => { try { return new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value || ""; } catch { return ""; } })();
    return (
        <div className={embedded ? "" : "max-w-3xl mx-auto px-6 sm:px-8 py-8"} data-testid="my-account-page">
            {!embedded && (
                <div className="text-center mb-6">
                    <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#9D4CDD]">My Account</div>
                    <h1 className="font-heading text-2xl font-black uppercase tracking-tight mt-1 text-white" data-testid="account-hero-name">{account.profile.name || user.email.split("@")[0]}</h1>
                </div>
            )}

            {/* ---------- HOME: favourite team picker ---------- */}
            {view === "home" && <div className="mb-5"><FollowedLeagues /></div>}
            {view === "home" && <FavouritesSection defaultLeague={form.fav_league} />}

            {/* ---------- OBJECTIVES ---------- */}
            {view === "objectives" && (() => {
                const firstName = ((account.profile.name || "").trim().split(/\s+/)[0]) || (user.email.split("@")[0]);
                const pred = (form.objectives && form.objectives.predictions) || {};
                const enabled = !!pred.enabled;
                const mode = pred.mode === "fixed" ? "fixed" : "pregame";
                const time = pred.time || "09:00";
                const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ""; } })();
                const tz = pred.tz || browserTz;
                const savePred = (patch) => {
                    const next = { predictions: { enabled, mode, time, tz: tz || browserTz, ...patch } };
                    setForm((s) => ({ ...s, objectives: next }));
                    save.mutate({ objectives: next });
                };
                const SCORING = [
                    { pts: 3, color: "#22C55E", label: "Exact correct score" },
                    { pts: 2, color: "#3B82F6", label: "Correct team to win" },
                    { pts: 1, color: "#A855F7", label: "Wrong team but correct game stage (Reg / OT / SO)" },
                    { pts: 0, color: "#6B7280", label: "Wrong team" },
                ];
                const RadioRow = ({ active, onClick, title, sub, testid }) => (
                    <button type="button" onClick={onClick} data-testid={testid}
                        className={`w-full text-left flex items-start gap-3 rounded-xl border px-4 py-3 transition-colors ${active ? "border-[#9D4CDD] bg-[#9D4CDD]/10" : "border-white/10 bg-black/25 hover:border-white/25"}`}>
                        <span className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 ${active ? "border-[#9D4CDD] bg-[#9D4CDD]" : "border-white/30"}`} />
                        <span className="min-w-0">
                            <span className="block text-sm font-bold text-white">{title}</span>
                            <span className="block text-xs text-white/50">{sub}</span>
                        </span>
                    </button>
                );
                return (
                    <div className="mx-auto max-w-2xl space-y-5" data-testid="account-objectives-view">
                        <Card icon={Target} title="Objectives" testid="account-objectives-card">
                            <p className="text-xs text-white/50 mb-4">Objectives are optional challenges you can opt into. Turn on a reminder so you never miss your picks.</p>
                            <div className="rounded-2xl border border-white/10 bg-black/25 p-4">
                                <div className="flex items-center justify-between gap-3 mb-3">
                                    <div>
                                        <div className="text-sm font-black text-white uppercase tracking-wide">Predictions</div>
                                        <div className="text-xs text-white/50">Predict every game's score. Earn points for how close you get.</div>
                                    </div>
                                    <button type="button" onClick={() => savePred({ enabled: !enabled })} data-testid="objective-predictions-enable"
                                        aria-pressed={enabled}
                                        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${enabled ? "bg-[#22C55E]" : "bg-white/15"}`}>
                                        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${enabled ? "left-[22px]" : "left-0.5"}`} />
                                    </button>
                                </div>
                                {/* scoring */}
                                <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-2">Scoring</div>
                                <div className="grid gap-2 mb-4">
                                    {SCORING.map((s) => (
                                        <div key={s.pts} className="flex items-center gap-3 rounded-lg bg-white/[0.03] px-3 py-2" data-testid={`objective-score-${s.pts}`}>
                                            <span className="inline-flex items-center justify-center h-7 w-7 rounded-md text-sm font-black text-white shrink-0" style={{ backgroundColor: s.color }}>{s.pts}</span>
                                            <span className="text-xs text-white/70">{s.label}</span>
                                        </div>
                                    ))}
                                </div>
                                {/* reminder */}
                                <div className={`transition-opacity ${enabled ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
                                    <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-2">Reminder</div>
                                    <div className="grid gap-2">
                                        <RadioRow active={mode === "pregame"} onClick={() => savePred({ mode: "pregame" })} testid="objective-mode-pregame"
                                            title="30 minutes before the first game" sub="One reminder, 30 min before the day's first puck drop" />
                                        <RadioRow active={mode === "fixed"} onClick={() => savePred({ mode: "fixed" })} testid="objective-mode-fixed"
                                            title="At a set time each day" sub={`Sent at your local time${tz ? ` · ${tz}` : ""}`} />
                                    </div>
                                    {mode === "fixed" && (
                                        <div className="mt-3 flex items-center gap-3">
                                            <label className="text-xs font-bold text-white/60 uppercase tracking-wider">Send at</label>
                                            <input type="time" value={time} data-testid="objective-time-input"
                                                onChange={(e) => savePred({ time: e.target.value })}
                                                className="rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-sm text-white focus:border-[#9D4CDD] outline-none" />
                                            <span className="text-xs text-white/40">{tz}</span>
                                        </div>
                                    )}
                                    <div className="mt-4 rounded-xl border border-white/10 bg-[#9D4CDD]/5 px-4 py-3">
                                        <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-1">Preview</div>
                                        <div className="text-sm text-white">Hi {firstName}, it's time to make your predictions.</div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                );
            })()}

            {/* ---------- NOTIFICATIONS ---------- */}
            {view === "notifications" && (
                <div className="mx-auto max-w-2xl" data-testid="account-notifications-view">
                    <AlertTeams />
                    {isOwner && <OwnerPushSection />}
                </div>
            )}

            {/* ---------- ACCOUNT (main page: profile + preferences, compact) ---------- */}
            {(view === "account" || view === "preferences") && (
                <div className="mx-auto max-w-xl space-y-3" data-testid="account-preferences-view">
                    <AccountProfile account={account} user={user} />
                    {embedded && <div className="flex justify-center">{switcher}</div>}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="account-prefs-compact">
                        <PrefRow label="Magic Numbers" hint="Points needed, or the total to reach" testid="pref-row-mag">
                            {[["needed", "Needed"], ["total", "Total"]].map(([k, l]) => <SmallPill key={k} on={(getPref("page_toggle", "mag_style") || "needed") === k} testid={`pref-mag-style-${k}`} onClick={() => setPref("page_toggle", "mag_style", k === "needed" ? null : k)}>{l}</SmallPill>)}
                        </PrefRow>
                        <PrefRow label="Predicted Records" hint="On game cards in every league" testid="pref-row-predrec">
                            {[["on", "On"], ["off", "Off"]].map(([k, l]) => <SmallPill key={k} on={(getPref("page_toggle", "pred_rec") || "on") === k} testid={`pref-predrec-${k}`} onClick={() => setPref("page_toggle", "pred_rec", k === "on" ? null : k)}>{l}</SmallPill>)}
                        </PrefRow>
                        <PrefRow label="Height" hint="Player bios & demographics" testid="pref-row-height">
                            {[["cm", "cm"], ["ft", "ft / in"]].map(([v, l]) => <SmallPill key={v} on={heightUnit === v} testid={`height-unit-${v}`} onClick={() => setHeightUnit(v)}>{l}</SmallPill>)}
                        </PrefRow>
                        <PrefRow label="Clock" hint={tzLabel ? `Local time (${tzLabel})` : "Local time"} testid="pref-row-clock">
                            {[["12h", "12h"], ["24h", "24h"]].map(([v, l]) => <SmallPill key={v} on={(form.time_format || "12h") === v} testid={`time-format-${v}`} onClick={() => pickTimeFormat(v)}>{l}</SmallPill>)}
                        </PrefRow>
                        <PrefRow label="Time Zone" hint={`Show the zone after times${tzLabel ? ` (${tzLabel})` : ""}`} testid="pref-row-tzlabel">
                            {[["on", "On"], ["off", "Off"]].map(([v, l]) => <SmallPill key={v} on={(form.tz_label || "off") === v} testid={`tz-label-${v}`} onClick={() => pickTzLabel(v)}>{l}</SmallPill>)}
                        </PrefRow>
                        <PrefRow label="Game Clock" hint="Goal 8:21 elapsed · 11:39 left" testid="pref-row-gameclock">
                            {[["elapsed", "Elapsed"], ["remaining", "Left"]].map(([v, l]) => <SmallPill key={v} on={(form.clock_mode || "elapsed") === v} testid={`clock-mode-${v}`} onClick={() => pickClockMode(v)}>{l}</SmallPill>)}
                        </PrefRow>
                        {isOwner && (
                            <PrefRow label="Account Setup" hint="Owner · walk through the new-account setup" testid="pref-row-setup">
                                <SmallPill testid="owner-test-setup" onClick={() => navigate("/setup?preview=1")}>Test setup</SmallPill>
                            </PrefRow>
                        )}
                        {isOwner && (
                            <PrefRow label="Install Banner" hint="Preview Add to Home Screen" testid="pref-row-pwa">
                                {[["ios", "iOS"], ["android", "Android"]].map(([v, l]) => <SmallPill key={v} testid={`pwa-preview-${v}`} onClick={() => window.dispatchEvent(new CustomEvent("rosterly:pwa-preview", { detail: v }))}>{l}</SmallPill>)}
                            </PrefRow>
                        )}
                    </div>
                    <FaceIdCard Card={Card} />
                    <QrDeviceCard Card={Card} />
                    <SignedInDevicesCard Card={Card} />
                </div>
            )}

            {/* ---------- FINANCES (owner only) ---------- */}
            {view === "finances" && isOwner && (
                <div data-testid="account-finances-view">
                    <MyFinances userKey={user?.email || user?.id || "me"} />
                </div>
            )}

            {/* ---------- SUBSCRIPTION (owner only) ---------- */}
            {view === "subscription" && isOwner && (
                <div className="mx-auto max-w-3xl" data-testid="account-subscription-view">
                    <Card icon={CreditCard} title="Subscription" testid="account-subscription-card">
                        <div className="flex items-center justify-between gap-3 rounded-xl bg-black/30 border border-white/10 px-4 py-3 mb-4" data-testid="subscription-current-plan">
                            <div><div className="text-[10px] uppercase tracking-wider text-white/45 font-bold">Current plan</div><div className="text-sm font-bold text-white">Rosterly Free</div></div>
                            <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full bg-[#5BD1B7]/15 text-[#5BD1B7]">Active</span>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                            {[{ id: "pro", name: "Pro", price: "$4.99/mo", perks: ["Ad-free", "Advanced stat tools", "Custom nav colours"] }, { id: "club", name: "Club", price: "$9.99/mo", perks: ["Everything in Pro", "Team analytics", "Early features"] }].map((p) => (
                                <div key={p.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={`subscription-plan-${p.id}`}>
                                    <div className="flex items-baseline justify-between mb-2"><span className="font-bold text-white text-lg">{p.name}</span><span className="text-sm text-[#C9A6F0] font-bold">{p.price}</span></div>
                                    <ul className="space-y-1.5 mb-3">{p.perks.map((k) => <li key={k} className="flex items-center gap-1.5 text-xs text-white/60"><Check className="h-3.5 w-3.5 text-[#5BD1B7]" />{k}</li>)}</ul>
                                    <button type="button" data-testid={`subscription-choose-${p.id}`} onClick={() => toast("Subscriptions launch soon — you'll be first to know.")} className="w-full py-2 rounded-lg bg-[#9D4CDD] hover:bg-[#B366F0] text-white font-bold uppercase tracking-wider text-[11px]">Choose {p.name}</button>
                                </div>
                            ))}
                        </div>
                        <p className="text-[11px] text-white/35 mt-3">Billing isn't live yet — these plans are a preview.</p>
                    </Card>
                </div>
            )}

            {/* ---------- REPORTS (owner only) ---------- */}
            {view === "reports" && isOwner && <div data-testid="account-reports-view"><Reports /></div>}

            {/* ---------- SECRET (URL-only, permission-gated) ---------- */}
            {view === "owner" && isOwner && <div data-testid="account-secret-owner"><SettingsAdmin /></div>}
            {view === "accounts" && (isOwner || hasAdminAccess) && <div data-testid="account-secret-accounts"><AccountsAdmin /></div>}
            {view === "settings" && isOwner && <div className="rounded-2xl border border-white/10 shadow-2xl p-4 sm:p-6" style={{ background: "#0B0D14" }} data-testid="account-settings-view"><SettingsAdmin /></div>}
            {view === "storage" && isOwner && <div className="rounded-2xl border border-white/10 shadow-2xl p-4 sm:p-6" style={{ background: "#0B0D14" }} data-testid="account-storage-view"><StorageCentre /></div>}
            {view === "leagues" && isOwner && <div className="rounded-2xl border border-white/10 shadow-2xl p-4 sm:p-6" style={{ background: "#0B0D14" }} data-testid="account-leagues-view"><Leagues /></div>}
        </div>
    );
}
